import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Orders = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            When someone purchases tickets, an order is created containing one
            or more ticket items. Each ticket item is assigned to an attendee by
            first name, last name, and email address. Purchasers can use the{' '}
            <Paragraph size='$5' fontWeight='$7'>
              For me
            </Paragraph>{' '}
            option to assign a ticket to themselves, or enter different attendee
            details to buy on behalf of others.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/events/event-page.png'
          alt={t`Event page showing the ticket purchase flow — orders are created when attendees complete checkout`}
          caption={t`Orders are created when attendees purchase tickets from the Event page`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='order-records' title={t`Order Records`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='clock'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Order statuses
                  </Paragraph>{' '}
                  — Paid ticket orders move through three statuses:{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    pending
                  </Paragraph>{' '}
                  while payment is processing,{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    succeeded
                  </Paragraph>{' '}
                  once payment is confirmed, and{' '}
                  <Paragraph size='$5' fontWeight='$7'>
                    refunded
                  </Paragraph>{' '}
                  after a successful refund. Orders can also be canceled if
                  checkout does not complete. An invoice number is automatically
                  assigned when the order succeeds.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='search'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Attendee records
                  </Paragraph>{' '}
                  — Event Settings includes attendee management and attendee
                  export tools. Use those records for registration review,
                  capacity planning, and event follow-up. Dedicated accounting
                  exports for full paid order records are not part of the
                  current Event Settings flow.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='money'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Refunds
                  </Paragraph>{' '}
                  — Event managers can refund succeeded ticket orders from the
                  attendee list in Event Settings. Open the relevant attendee
                  record and use the Refund Order action. Guild issues the
                  refund through the connected Stripe account, marks the order
                  as refunded, removes the affected attendees from the Event,
                  and emails the purchaser.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='link'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Exporting attendee data
                  </Paragraph>{' '}
                  — Export attendee data from Event Settings for planning,
                  registration review, and follow-up. This is attendee data, not
                  a full accounting export of paid order records.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='purchase-flow-link' title={t`Attendee Experience`}>
          <Paragraph size='$5'>
            <Trans>
              For more on the attendee experience when purchasing tickets, see{' '}
              <Link href='/docs/events/purchase-flow'>
                Attendee Purchase Flow
              </Link>
              .
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Orders
